/** Canonical GC-controlled introduction workflow presentation (Increment 0). */

export const CANONICAL_STAGES = [
  "ADDED_TO_QUEUE",
  "AWAITING_GC_INTRODUCTION_APPROVAL",
  "GC_INTRODUCTION_APPROVED",
  "INTRODUCTION_DRAFT_READY",
  "INTRODUCTION_SENT",
  "TRADE_PARTNER_ENGAGED",
  "SESSION_SCHEDULED",
] as const;

export type CanonicalStage = (typeof CANONICAL_STAGES)[number];

export const STAGE_LABELS: Record<CanonicalStage, string> = {
  ADDED_TO_QUEUE: "Added to Queue",
  AWAITING_GC_INTRODUCTION_APPROVAL: "Awaiting GC Introduction Approval",
  GC_INTRODUCTION_APPROVED: "GC Introduction Approved",
  INTRODUCTION_DRAFT_READY: "Introduction Draft Ready",
  INTRODUCTION_SENT: "Introduction Sent",
  TRADE_PARTNER_ENGAGED: "Trade Partner Engaged",
  SESSION_SCHEDULED: "Session Scheduled",
};

export const STAGE_DETAILS: Record<CanonicalStage, string> = {
  ADDED_TO_QUEUE: "Trade partner was added to the GC-controlled Introduction Queue.",
  AWAITING_GC_INTRODUCTION_APPROVAL:
    "The general contractor is reviewing the source-backed rationale and deciding whether ConstructPay may prepare the introduction.",
  GC_INTRODUCTION_APPROVED:
    "The GC approved the introduction. ConstructPay may prepare the draft, but no message has been sent.",
  INTRODUCTION_DRAFT_READY: "Introduction language is ready for GC review and controlled delivery.",
  INTRODUCTION_SENT: "The general contractor recorded that the approved introduction was sent.",
  TRADE_PARTNER_ENGAGED: "The trade partner responded and chose to engage with ConstructPay.",
  SESSION_SCHEDULED: "A ConstructPay readiness or discovery session has been scheduled.",
};

export const MODAL_TITLES: Record<CanonicalStage, string> = {
  ADDED_TO_QUEUE: "Begin GC Review",
  AWAITING_GC_INTRODUCTION_APPROVAL: "GC Introduction Approval",
  GC_INTRODUCTION_APPROVED: "Prepare Introduction Draft",
  INTRODUCTION_DRAFT_READY: "Review Draft & Record Send",
  INTRODUCTION_SENT: "Record Trade Partner Response",
  TRADE_PARTNER_ENGAGED: "Schedule Readiness Session",
  SESSION_SCHEDULED: "Introduction Workflow Complete",
};

export const QUEUE_PRIMARY_LABELS: Record<CanonicalStage, string> = {
  ADDED_TO_QUEUE: "Begin GC review",
  AWAITING_GC_INTRODUCTION_APPROVAL: "Preview & approve",
  GC_INTRODUCTION_APPROVED: "Prepare introduction",
  INTRODUCTION_DRAFT_READY: "Review draft / record send",
  INTRODUCTION_SENT: "Record response",
  TRADE_PARTNER_ENGAGED: "Schedule session",
  SESSION_SCHEDULED: "View completed flow",
};

export interface QueueWorkflowItem {
  id: string;
  /** Null while a source-pending trade partner has no TradeTapp company record yet. */
  vendor_id: string | null;
  company_name: string;
  metric_key: string;
  source_snapshot: string;
  status: string;
  workflow_stage?: string;
  stage_label?: string;
  owner?: string;
  next_action?: string;
  progress_position?: number;
  progress_pct?: number;
  outreach_occurred?: boolean;
  governance_message?: string;
  primary_action_label?: string;
  permitted_next_status?: string | null;
  notes?: string | null;
  manuallyAdded?: boolean;
  created_by?: string | null;
  created_by_display?: string | null;
  created_at?: string;
  provisional_trade_partner_id?: string | null;
  source_pending?: boolean;
  source_status?: string | null;
  source_evidence?: string | null;
  primary_contact_name?: string | null;
  primary_contact_email?: string | null;
  reconciled_at?: string | null;
}

export const AUTHENTICATED_USER_LABEL = "authenticated user";

/**
 * Customer-facing Created-by label. Prefers the backend's source-backed
 * display value. Never surfaces a raw Firebase UID as the primary label.
 */
export function createdByDisplayLabel(item: {
  created_by?: string | null;
  created_by_display?: string | null;
}): string | null {
  const display = (item.created_by_display ?? "").trim();
  if (display) {
    return display;
  }
  const raw = (item.created_by ?? "").trim();
  if (!raw) {
    return null;
  }
  if (raw.includes("@")) {
    return raw;
  }
  return AUTHENTICATED_USER_LABEL;
}

export function stageIndex(stage: string | undefined | null): number {
  const idx = CANONICAL_STAGES.indexOf((stage || "") as CanonicalStage);
  return idx >= 0 ? idx : 0;
}

export function outreachCopy(outreachOccurred: boolean | undefined): string {
  return outreachOccurred ? "Introduction has been sent" : "No outreach triggered";
}

/** Prefer the server's explanation (e.g. reconcile-first conflict) over the transport error text. */
export function queueAddErrorMessage(e: any): string {
  const serverMessage = e?.response?.data?.error;
  if (typeof serverMessage === "string" && serverMessage.trim()) return serverMessage;
  return e?.message ?? "Failed to add to queue";
}

export function queuePrimaryLabel(stage: string | undefined | null): string {
  const s = (stage || "ADDED_TO_QUEUE") as CanonicalStage;
  return QUEUE_PRIMARY_LABELS[s] || "Preview next step";
}

export function modalLanguage(companyName: string, stage: string): string {
  const s = (stage || "ADDED_TO_QUEUE") as CanonicalStage;
  if (stageIndex(s) >= 4) {
    return `Introduction record for ${companyName}

The GC-approved introduction has been recorded as sent. The next controlled step is to record whether the trade partner chooses to engage.

No underwriting, financial decision, or partner commitment is implied by this workflow status.`;
  }
  if (s === "INTRODUCTION_DRAFT_READY") {
    return `Hi ${companyName},

Your general contractor is introducing ConstructPay as an additional resource that may help selected trade partners strengthen financial readiness, bonding capacity, and access to working capital.

No action is required until you choose to engage. ConstructPay will provide a secure, trade-partner-controlled view of readiness insights and next-step options.

Regards,
General Contractor Team`;
  }
  if (s === "GC_INTRODUCTION_APPROVED") {
    return `Approved positioning for ${companyName}

ConstructPay may now prepare the GC-reviewed introduction draft. This approval does not send a message and does not initiate underwriting, notification, or partner engagement.`;
  }
  return `Hi ${companyName},

Your general contractor is considering an introduction to ConstructPay as an additional resource that may help selected trade partners strengthen financial readiness, bonding capacity, and access to working capital.

No action is required and no outreach will occur unless the general contractor approves the introduction.

Regards,
General Contractor Team`;
}

export function metricContextLabel(metricKey: string | undefined): string {
  if (!metricKey) return "Source Pending";
  return metricKey.replace(/_/g, " ").toLowerCase();
}

export function projectContextLabel(_item: QueueWorkflowItem): string {
  // No project column exists on queue rows; remain truthful.
  return "Source Pending";
}

export function progressPercent(item: QueueWorkflowItem): number {
  if (typeof item.progress_pct === "number" && Number.isFinite(item.progress_pct)) {
    return item.progress_pct;
  }
  const idx = stageIndex(item.workflow_stage);
  return Math.round((idx / (CANONICAL_STAGES.length - 1)) * 1000) / 10;
}
